import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Grid from '../components/Grid';
import { FaEye, FaDownload } from "react-icons/fa";
import { DataGrid } from '@mui/x-data-grid';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function DebitNote({ onAddDebitNoteClick }) {
  const [debitNotes, setDebitNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDebitNote, setSelectedDebitNote] = useState(null);
  const [selectedSearchField, setSelectedSearchField] = useState('debit_note_code');

  useEffect(() => {
    const fetchDebitNotes = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Authentication token is missing. Please log in again.');
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/debitNotes/get`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const sortedDebitNotes = response.data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        setDebitNotes(sortedDebitNotes);
      } catch (error) {
        console.error('Error fetching debit notes:', error);
        toast.error('Error fetching debit notes');
      }
    };

    fetchDebitNotes();
  }, []);

  const getValueByPath = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const filteredDebitNotes = debitNotes
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .filter((note) => {
      if (searchTerm.trim() === '') return true;
      const fieldValue = getValueByPath(note, selectedSearchField);
      if (!fieldValue) return false;
      return fieldValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });

  const fetchDebitNoteDetails = async (_id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token is missing. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/debitNotes/get/${_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSelectedDebitNote(response.data);
    } catch (error) {
      console.error('Error fetching debit note details:', error);
      toast.error('Failed to fetch debit note details');
    }
  };

  const handleDownloadExcel = async () => {
    const dataToExport =
      searchTerm.trim().length > 0 ? filteredDebitNotes : debitNotes;

    if (dataToExport.length === 0) {
      toast.warn("No debit note data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Debit Notes");

    const headerRow = [
      "Debit Note Code",
      "Supplier Code",
      "Supplier Name",
      "Issue Date",
      "Debited Amount (LKR)",
      "Balance Amount (LKR)",
      "Status",
      "Created By"
    ];
    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD966" } };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((item) => {
      worksheet.addRow([
        item.debit_note_code,
        item.supplier_id?.supplier_code || "N/A",
        item.supplier_id?.supplier_name || "N/A",
        item.issue_date || "N/A",
        Number(item.total_debit_amount || 0).toFixed(2),
        Number(item.balance || 0).toFixed(2),
        item.status,
        item.createdBy?.username || "Unknown"
      ]);
    });

    worksheet.columns.forEach((column) => {
      column.width = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "debit_notes.xlsx");
  };

  const columns = [
    { field: 'debit_note_code', headerName: 'Debit Note Code', flex: 1 },
    { 
      field: 'issue_date', 
      headerName: 'Issue Date', 
      flex: 1,
      renderCell: (params) => (
        <span>
          {params.value ? new Date(params.value).toLocaleDateString("en-GB") : "N/A"}
        </span>
      ),
    },
    { 
      field: 'supplier_code', 
      headerName: 'Supplier Code', 
      flex: 1,
      renderCell: (params) => (
        <span>{params.row.supplier_id?.supplier_code || "No Supplier Code"}</span>
      ),
    },
    { 
      field: 'supplier_name', 
      headerName: 'Supplier Name', 
      flex: 1,
      renderCell: (params) => (
        <span>{params.row.supplier_id?.supplier_name || "No Supplier Name"}</span>
      ),
    },
    { 
      field: 'total_debit_amount', 
      headerName: 'Debited Amount (LKR)', 
      flex: 1, 
      headerAlign: "right", 
      align: "right", 
      renderCell: (params) => `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` 
    },
    { 
      field: 'balance', 
      headerName: 'Balance Amount (LKR)', 
      flex: 1, 
      headerAlign: "right", 
      align: "right", 
      renderCell: (params) => `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` 
    },
    { 
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const status = params.row.status;
        const statusClassMap = {
          "Unsettled": "bg-gray-600 text-white",
          "Partially Settled": "bg-[#8BC34A] text-white",
          "Fully Settled": "bg-[#90A4AE] text-white",
        };
        const statusClass = statusClassMap[status] || "bg-gray-500 text-white";
        return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>;
      },
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span>{params.row.createdBy?.username || "Unknown"}</span>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex justify-center items-center p-3 space-x-3">
          <button
            onClick={() => fetchDebitNoteDetails(params.row._id)}
            className="bg-[#FBC02D] text-white p-2 flex items-center justify-center"
          >
            <FaEye className="text-white" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Debit Notes</h1>
          <button
            onClick={onAddDebitNoteClick}
            className="bg-green-700 text-white px-4 py-2 rounded-lg"
          >
            Add Debit Note
          </button>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="debit_note_code">Debit Note Code</option>
                <option value="supplier_id.supplier_code">Supplier Code</option>
                <option value="supplier_id.supplier_name">Supplier Name</option>
                <option value="issue_date">Issue Date</option>
                <option value="total_debit_amount">Amount</option>
                <option value="balance">Balance</option>
                <option value="status">Status</option>
                <option value="createdBy.username">Created By</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search debit notes"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Excel Download */}
            <button
              onClick={handleDownloadExcel}
              className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <FaDownload className="mr-2" />
              Download Excel
            </button>
          </div>

          <Grid rows={filteredDebitNotes} columns={columns} getRowId={(row) => row._id} />

          {/* Debit Note Details Modal */}
          {selectedDebitNote && (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
              <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative max-h-[90vh] overflow-y-auto">
                <button
                  onClick={() => setSelectedDebitNote(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-lg hover:bg-red-700"
                >
                  &times;
                </button>

                <h2 className="text-3xl font-bold mb-4">Debit Note Details</h2>

                <div className="mb-4">
                  <label className="block font-semibold">Debit Note Code</label>
                  <input type="text" value={selectedDebitNote.debit_note_code || ""} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="mb-4">
                  <label className="block font-semibold">Supplier Code</label>
                  <input type="text" value={selectedDebitNote.supplier_id?.supplier_code || "Unknown"} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="mb-4">
                  <label className="block font-semibold">Supplier Name</label>
                  <input type="text" value={selectedDebitNote.supplier_id?.supplier_name || "Unknown"} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="mb-4">
                  <label className="block font-semibold">Debited Amount</label>
                  <input type="text" value={selectedDebitNote.total_debit_amount || 0} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="mb-4">
                  <label className="block font-semibold">Balance Amount</label>
                  <input type="text" value={selectedDebitNote.balance || 0} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="mb-4">
                  <label className="block font-semibold">Issue Date</label>
                  <input type="text" value={
                      selectedDebitNote.issue_date
                        ? selectedDebitNote.issue_date.slice(0, 10)
                        : ""
                    } readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="mb-4">
                  <label className="block font-semibold">Reason</label>
                  <textarea value={selectedDebitNote.reason || ""} readOnly rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none" />
                </div>

                {/* Linked Purchase Invoices */}
                <h3 className="text-lg font-semibold mt-4 mb-2">Invoices</h3>
                <div style={{ height: 400, width: "100%" }}>
                  <DataGrid
                    rows={selectedDebitNote.applied_invoices.map((invoice, index) => ({
                      id: index + 1,
                      invoiceID: invoice.invoice_id?.invoice_id,
                      debit_amount: invoice.debit_amount,
                    }))}
                    columns={[
                      { field: "id", headerName: "No", flex: 0.3 },
                      { field: "invoiceID", headerName: "Invoice ID", flex: 1 },
                      { field: "debit_amount", headerName: "Applied Amount (LKR)", flex: 1, headerAlign: "right", align: "right", renderCell: (params) => `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value)}` },
                    ]}
                    pageSize={10}
                  />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default DebitNote;
